// All coin numbers live here. Values marked PLACEHOLDER were not given in the
// design doc yet; the creator will replace them later.

export const STARTING_COINS = 500;


/** Default ante ("minimum entry cost"). Host can change per lobby. */
export const DEFAULT_ANTE = 5;
export const MIN_ANTE = 1;
export const MAX_ANTE = 500;

/** Coin chip denominations (visual only — all math is in whole coins). */
export const COIN_DENOMINATIONS = [
  { id: "copper", name: "Copper", value: 1 },
  { id: "lead", name: "Lead", value: 5 },
  { id: "silver", name: "Silver", value: 10 },
  { id: "platinum", name: "Platinum", value: 25 },
  { id: "impure_gold", name: "Impure Gold", value: 100 },
  { id: "rose_gold", name: "Rose Gold", value: 250 },
  { id: "pure_gold", name: "Pure Gold", value: 500 },
] as const;

export type Rarity = "common" | "rare" | "wicked" | "legendary" | "mythic" | "wow";

/** Orb tiers, cheapest first. */
export type OrbTier = "low" | "middle" | "high";
export const ORB_TIERS: readonly OrbTier[] = ["low", "middle", "high"];

/** Orb names shown to players (creator, round 6). */
export const ORB_NAMES: Record<OrbTier, string> = {
  low: "Apprentice Orb",
  middle: "Magus Orb",
  high: "Archmage Orb",
};

/** Orb prices in coins (creator, round 7). */
export const ORB_PRICES: Record<OrbTier, number> = {
  low: 1000,
  middle: 3000,
  high: 6000,
};

/**
 * Orb odds in percent per rarity; each tier adds up to 100 (creator, round 6).
 * Round 9: WOW is a tenth of what it was (1 / 2 / 4 % became 0.1 / 0.2 / 0.4 %)
 * and the rest went to common. The shop shows WOW as "< 1%" (WOW_ODDS_LABEL).
 */
export const ORB_ODDS: Record<OrbTier, Record<Rarity, number>> = {
  low: { common: 64.9, rare: 18, wicked: 11, legendary: 4, mythic: 2, wow: 0.1 },
  middle: { common: 45.8, rare: 25, wicked: 18, legendary: 7, mythic: 4, wow: 0.2 },
  high: { common: 23.6, rare: 30, wicked: 24, legendary: 14, mythic: 8, wow: 0.4 },
};

/** How the shop shows every orb's WOW chance. */
export const WOW_ODDS_LABEL = "< 1%";

/** An orb that gives an item you already own pays back this share of the item's shop price (rounded down). */
export const ORB_DUPLICATE_REFUND_RATE = 1 / 3;

/** A weekly login reward: coins, or one free pull of an orb tier. */
export type WeeklyReward = { kind: "coins"; coins: number } | { kind: "orb"; tier: OrbTier };

/**
 * Weekly login rewards, Monday first (index 0 = Monday … 6 = Sunday). Each
 * day's reward can be claimed once on that day of the player's calendar (the
 * day turns at their local midnight); a missed day is gone. Replaced the 100-coin daily claim.
 */
export const WEEKLY_REWARDS: readonly WeeklyReward[] = [
  { kind: "coins", coins: 100 },
  { kind: "orb", tier: "low" },
  { kind: "coins", coins: 150 },
  { kind: "orb", tier: "middle" },
  { kind: "coins", coins: 250 },
  { kind: "orb", tier: "high" },
  { kind: "coins", coins: 200 },
];

export const WEEKDAY_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"] as const;

/** Place of a moment's UTC day in the week: 0 = Monday … 6 = Sunday. */
export function utcWeekday(nowMs: number): number {
  return (new Date(nowMs).getUTCDay() + 6) % 7;
}

/** PLACEHOLDER: support rewards. */
export const REFERRAL_REWARD_NEW_USER = 100;
export const REFERRAL_REWARD_REFERRER = 100;
export const DISCORD_JOIN_REWARD = 100;

/**
 * A player whose coins run out (0, from anything: a lost hand, slots, the
 * shop…) gets this many back so they can keep playing. While their bets are
 * still in a hand's pot the refill waits until that hand is over.
 */
export const BANKRUPT_REFILL_COINS = 100;
